// Enhanced sticky positioning hook with scroll detection
// Provides smooth sticky behavior and scroll-based effects

import { useEffect, useState, useCallback, RefObject } from 'react'

interface UseStickyOptions {
  threshold?: number
  rootMargin?: string
  triggerScrolled?: boolean
  onStickChange?: (isStuck: boolean) => void
}

interface UseStickyReturn {
  isSticky: boolean
  isScrolled: boolean
  scrollY: number
  direction: 'up' | 'down' | null
}

/**
 * Enhanced sticky positioning hook
 * Tracks sticky state, scroll position, and scroll direction
 */
export const useSticky = (
  ref: RefObject<HTMLElement>,
  options: UseStickyOptions = {},
): UseStickyReturn => {
  const { threshold = 0, rootMargin = '0px', triggerScrolled = true, onStickChange } = options

  const [isSticky, setIsSticky] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)
  const [scrollY, setScrollY] = useState(0)
  const [direction, setDirection] = useState<'up' | 'down' | null>(null)
  const [lastScrollY, setLastScrollY] = useState(0)

  // Handle scroll events
  const handleScroll = useCallback(() => {
    const currentScrollY = window.scrollY

    setScrollY(currentScrollY)

    // Determine scroll direction
    if (currentScrollY > lastScrollY) {
      setDirection('down')
    } else if (currentScrollY < lastScrollY) {
      setDirection('up')
    }

    setLastScrollY(currentScrollY)

    // Set scrolled state based on threshold
    if (triggerScrolled) {
      setIsScrolled(currentScrollY > threshold)
    }
  }, [lastScrollY, threshold, triggerScrolled])

  // Throttled scroll handler for performance
  const throttledHandleScroll = useCallback(() => {
    requestAnimationFrame(handleScroll)
  }, [handleScroll])

  useEffect(() => {
    // Initial scroll position
    handleScroll()

    // Add scroll listener with passive option for performance
    window.addEventListener('scroll', throttledHandleScroll, { passive: true })

    return () => {
      window.removeEventListener('scroll', throttledHandleScroll)
    }
  }, [throttledHandleScroll, handleScroll])

  // Intersection Observer for sticky detection
  useEffect(() => {
    const element = ref.current
    if (!element) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        const newIsSticky = entry.intersectionRatio < 1
        setIsSticky(newIsSticky)

        if (onStickChange) {
          onStickChange(newIsSticky)
        }
      },
      {
        threshold: [1],
        rootMargin,
      },
    )

    observer.observe(element)

    return () => {
      observer.unobserve(element)
    }
  }, [ref, rootMargin, onStickChange])

  return {
    isSticky,
    isScrolled,
    scrollY,
    direction,
  }
}

/**
 * Hook for navbar sticky behavior
 */
export const useNavbarSticky = () => {
  const [isScrolled, setIsScrolled] = useState(false)
  const [isVisible, setIsVisible] = useState(true)
  const [lastScrollY, setLastScrollY] = useState(0)

  const handleScroll = useCallback(() => {
    const currentScrollY = window.scrollY

    // Show/hide navbar based on scroll direction
    if (currentScrollY > lastScrollY && currentScrollY > 100) {
      // Scrolling down - hide navbar
      setIsVisible(false)
    } else {
      // Scrolling up - show navbar
      setIsVisible(true)
    }

    // Set scrolled state for styling
    setIsScrolled(currentScrollY > 10)
    setLastScrollY(currentScrollY)
  }, [lastScrollY])

  const throttledHandleScroll = useCallback(() => {
    requestAnimationFrame(handleScroll)
  }, [handleScroll])

  useEffect(() => {
    window.addEventListener('scroll', throttledHandleScroll, { passive: true })

    return () => {
      window.removeEventListener('scroll', throttledHandleScroll)
    }
  }, [throttledHandleScroll])

  return {
    isScrolled,
    isVisible,
    scrollY: lastScrollY,
  }
}

/**
 * Hook for dynamic z-index management
 */
export const useZIndex = (baseZIndex: number = 1) => {
  const [zIndex, setZIndex] = useState(baseZIndex)

  const elevate = useCallback((amount: number = 10) => {
    setZIndex((prev) => prev + amount)
  }, [])

  const lower = useCallback((amount: number = 10) => {
    setZIndex((prev) => Math.max(1, prev - amount))
  }, [])

  const reset = useCallback(() => {
    setZIndex(baseZIndex)
  }, [baseZIndex])

  return {
    zIndex,
    elevate,
    lower,
    reset,
  }
}

/**
 * Utility function to add sticky scroll classes
 */
export const addStickyScrollClasses = (element: HTMLElement, isScrolled: boolean) => {
  if (isScrolled) {
    element.classList.add('sticky-scrolled', 'sticky-enhanced')
  } else {
    element.classList.remove('sticky-scrolled', 'sticky-enhanced')
  }
}

/**
 * Utility function to calculate safe sticky top position
 */
export const calculateStickyTop = (precedingElements: string[] = []): number => {
  let totalHeight = 0

  precedingElements.forEach((selector) => {
    const element = document.querySelector(selector)
    if (element) {
      totalHeight += element.getBoundingClientRect().height
    }
  })

  return totalHeight
}
